import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { DriverApi, ManifestRecord } from "../driver-api.ts";
import { CUSTODY_NOTE, handoffChip, handoffRows, handoffSub, handoffTitle } from "../handoff.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Handoff` — the sealed manifest a courier is collecting at a hub.
 *
 * `handoff.ts` beside this file holds every reading and the reasons, including why this screen was
 * recorded against the wrong service for so long. Three things about the frame are decisions.
 *
 * **It lists rather than demanding an identifier.** LOG-07's listing filters by status, so the
 * screen asks for sealed manifests and takes the one departing soonest — which is the order the
 * listing already answers in. A courier at a hub is collecting the next thing leaving it, and a
 * screen that could only be reached by a link somebody else minted would not be reachable at all.
 *
 * **Both buttons are drawn and both are refused, for different reasons.** `Scan next parcel` is a
 * real operation — LOG-06 takes a device scan, and the driver app's own `5 Scan` screen presses it
 * — but nothing joins a scan to a manifest, so a button here could send the scan and then have
 * nothing to show for it on this page. `Report missing` has no operation at all.
 *
 * **`art:'scan'` and `on:1` are the design's own flags.** A courier collecting a manifest is on
 * shift, which is the one screen in this file's design where that is true.
 */

export interface HandoffProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  /** A specific manifest, when the address names one. Otherwise the next sealed one. */
  readonly manifestId: string | undefined;
}

const SCAN_UNAVAILABLE =
  "Scanning works — LOG-06 takes a device scan — but nothing joins a scan to a manifest, so this page could send one and have nothing to show for it. The Scan screen is where a parcel is scanned today.";

const REPORT_MISSING_UNAVAILABLE =
  "Nothing publishes a way to report a parcel missing against a manifest. LOG-06 records scans that happened and models no absence.";

export function Handoff({ api, tenantId, manifestId }: HandoffProps): ReactNode {
  const [manifest, setManifest] = useState<ManifestRecord | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    setState({ kind: "loading" });
    const asked =
      manifestId === undefined
        ? api.sealedManifests({ tenantId }).then((manifests) => manifests[0])
        : api.manifest({ tenantId, manifestId });
    asked
      .then((answer) => {
        setManifest(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, ["manifest:read"])));
  }, [api, tenantId, manifestId]);

  useEffect(load, [load]);

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="handoff"
        state={state}
        onRetry={load}
        purpose="Looking up the manifest waiting at this hub."
      />
    );
  }

  if (manifest === undefined) {
    /*
     * A read that worked and found nothing. Not an error: a hub with nothing sealed has nothing to
     * hand over, and a courier told something went wrong would go looking for a fault.
     */
    return (
      <StateScreen
        screen="handoff"
        chip={{ class: "idle", label: "nothing sealed" }}
        title="There is nothing to collect"
        sub="No manifest has been sealed for collection. When a hub seals one, this is where it will be."
      />
    );
  }

  const title = handoffTitle(manifest);

  return (
    <PhoneShell
      screen="handoff"
      /* `art:'scan'` — the design's own, and the same disc the Scan screen draws. */
      art={<HeroDisc glyph="qr_code_scanner" caption="Scan" />}
      /* `on:1`: a courier collecting a manifest is on shift. The design's own flag. */
      online={true}
      earningsLabel={null}
      chip={handoffChip(manifest)}
      title={title.text}
      sub={handoffSub(manifest)}
      rows={handoffRows(manifest)}
      note={{ kind: "pending", text: CUSTODY_NOTE }}
      actions={[
        {
          label: "Scan next parcel",
          glyph: "qr_code_scanner",
          primary: true,
          unavailable: SCAN_UNAVAILABLE,
        },
        {
          label: "Report missing",
          glyph: "report",
          primary: false,
          unavailable: REPORT_MISSING_UNAVAILABLE,
        },
      ]}
      {...(title.designed ? {} : { flag: "wording not in the design" })}
    />
  );
}
